/**
 * useAnalytics.js
 *
 * Composable centralizado para tracking de eventos en Google Analytics 4.
 * Todos los eventos de conversión de Coolliving pasan por aquí.
 *
 * Uso:
 *   import { useAnalytics } from '@/composables/useAnalytics'
 *   const { trackSearch, trackViewColiving, ... } = useAnalytics()
 *
 * Eventos implementados:
 *   trackSearch()         → búsqueda de colivings
 *   trackZeroResults()    → búsqueda sin resultados
 *   trackLoadMore()       → cargar más resultados (paginación)
 *   trackViewColiving()   → ver detalle de un coliving
 *   trackApply()          → enviar solicitud (lead principal)
 *   trackSignUp()         → registro de nuevo usuario
 *   trackBeginCheckout()  → inicio de pago Stripe
 *   trackPurchase()       → pago completado
 *   track()               → evento genérico
 */

import { trackGoogleAdsConversion, GOOGLE_ADS_CONVERSIONS } from '@/utils/analyticsConsent'

export function useAnalytics() {

  /**
   * Envía un evento a GA4.
   * Solo se ejecuta si gtag está disponible (no rompe en SSR ni en tests).
   */
  function track(eventName, params = {}) {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', eventName, {
        ...params,
        event_timestamp: Date.now(),
      });
    }
  }

  /**
   * Envía evento tanto a GA4 como al backend de analytics.
   * Usado para eventos que queremos en el email diario y en AdminAnalytics.
   */
  function trackWithBackend(eventName, params = {}) {
    // 1. Enviar a Google Analytics
    track(eventName, params);
    
    // 2. Enviar al backend (fire-and-forget)
    if (typeof window !== 'undefined') {
      fetch('/api/analytics/interaction-event', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event: eventName,
          props: params
        })
      }).catch(() => {
        // Silenciar errores de analytics para no romper UX
      });
    }
  }

  /**
   * Envía un evento estándar al Meta Pixel.
   * Solo se ejecuta si fbq está disponible.
   */
  function trackMeta(eventName, params = {}) {
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('track', eventName, params);
    }
  }

  /**
   * Envía un evento personalizado al Meta Pixel.
   * Útil para eventos propios (no estándar de Meta).
   */
  function trackMetaCustom(eventName, params = {}) {
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('trackCustom', eventName, params);
    }
  }

  // ─────────────────────────────────────────────────────────────────────
  // 1. BÚSQUEDA DE COLIVINGS
  //    Cuándo: al ejecutar performSearch con resultados (nueva búsqueda)
  //    Dónde:  ColivingAdvancedSearch.vue → performSearch()
  // ─────────────────────────────────────────────────────────────────────
  function trackSearch({
    ciudad = '',
    totalResultados = 0,
    isLoggedIn = false,
    filtros = {},
  } = {}) {
    track('search', {
      search_term: ciudad || 'todas',
      ciudad,
      total_resultados: totalResultados,
      usuario_logueado: isLoggedIn,
      filtro_precio_max: filtros.maxPrice || null,
      filtro_precio_min: filtros.minPrice || null,
      filtro_habitaciones: filtros.rooms || null,
      filtro_mascotas: filtros.petsAllowed || null,
      filtro_parejas: filtros.couplesAllowed || null,
      filtro_estudiantes: filtros.forStudents || null,
      filtro_estancia_min: filtros.minStayMonths || null,
      filtro_tipo_contrato: filtros.contractType || null,
      filtro_ordenar: filtros.sortBy || null,
    });
    // Meta: Search es evento estándar — alimenta audiencias de retargeting
    trackMeta('Search', { search_string: ciudad || 'todas' });
  }

  // ─────────────────────────────────────────────────────────────────────
  // 1b. BÚSQUEDA SIN RESULTADOS
  //     Cuándo: performSearch devuelve 0 resultados
  //     Dónde:  ColivingAdvancedSearch.vue → performSearch()
  //     Valor:  Identifica ciudades/combinaciones sin oferta → oportunidad
  // ─────────────────────────────────────────────────────────────────────
  function trackZeroResults({ ciudad = '', filtros = {} } = {}) {
    track('search_zero_results', {
      search_term: ciudad || 'todas',
      ciudad,
      filtro_precio_max: filtros.maxPrice || null,
      filtro_precio_min: filtros.minPrice || null,
      filtro_habitaciones: filtros.rooms || null,
      filtro_mascotas: filtros.petsAllowed || null,
      filtro_parejas: filtros.couplesAllowed || null,
    });
  }

  // ─────────────────────────────────────────────────────────────────────
  // 1c. CARGAR MÁS RESULTADOS (paginación)
  //     Cuándo: usuario hace click en "Ver más" (loadMoreResults)
  //     Dónde:  ColivingAdvancedSearch.vue → loadMoreResults()
  //     Valor:  Mide engagement; página 2+ = usuario muy interesado
  // ─────────────────────────────────────────────────────────────────────
  function trackLoadMore({ ciudad = '', pagina = 2, totalResultados = 0 } = {}) {
    track('search_load_more', {
      ciudad,
      pagina,
      total_resultados: totalResultados,
    });
  }

  // ─────────────────────────────────────────────────────────────────────
  // 2. VER DETALLE DE COLIVING
  //    Cuándo: cuando se carga la página de detalle de un coliving
  //    Dónde:  ViewColivingPublic.vue → loadColiving() (éxito)
  // ─────────────────────────────────────────────────────────────────────
  function trackViewColiving({ id = '', nombre = '', ciudad = '', precioDesde = null } = {}) {
    track('select_item', {
      item_list_name: 'colivings',
      items: [{
        item_id: id,
        item_name: nombre,
        item_category: 'coliving',
        item_category2: ciudad,
        price: precioDesde,
      }],
      coliving_id: id,
      coliving_nombre: nombre,
      ciudad,
      precio_desde: precioDesde,
    });
    // Meta: ViewContent — señal de interés alto, alimenta audiencias similares
    trackMeta('ViewContent', {
      content_ids: [id],
      content_name: nombre,
      content_category: 'coliving',
      content_type: 'product',
      value: precioDesde || 0,
      currency: 'EUR',
    });
    // Persistir visita en Firestore para analytics del agente (no bloqueante)
    if (id) {
      const utm = (() => { try { return JSON.parse(localStorage.getItem('coolliving_utm') || '{}') } catch { return {} } })();
      const source = utm.utm_source || (document.referrer.includes('google') ? 'google' : utm.fbclid ? 'meta' : 'direct');
      fetch('/api/agents/analytics/coliving-view', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ colivingId: id, ciudad, source }),
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      }).catch(() => {});
    }
  }

  // ─────────────────────────────────────────────────────────────────────
  // 3. SOLICITUD ENVIADA (lead principal del negocio)
  //    Cuándo: cuando submitApplication tiene éxito
  //    Dónde:  ColivingRoomRequest.vue → submitApplication()
  // ─────────────────────────────────────────────────────────────────────
  function trackApply({ colivingId = '', colivingNombre = '', ciudad = '', habitacionTipo = '', autenticado = false } = {}) {
    track('generate_lead', {
      coliving_id: colivingId,
      coliving_nombre: colivingNombre,
      ciudad,
      habitacion_tipo: habitacionTipo,
      usuario_autenticado: autenticado,
      currency: 'EUR',
      value: 1,
    });
    // Meta: Lead — evento de conversión principal del negocio
    trackMeta('Lead', {
      content_name: colivingNombre,
      content_category: ciudad,
      content_ids: [colivingId],
      currency: 'EUR',
      value: 1,
    });
    // Google Ads: conversión principal — solicitud de habitación enviada
    // Permite a Google Ads optimizar Smart Bidding hacia solicitudes reales
    if (GOOGLE_ADS_CONVERSIONS.solicitud) {
      trackGoogleAdsConversion(GOOGLE_ADS_CONVERSIONS.solicitud, {
        value: 12,      // valor estimado del lead en EUR (ajustar según CPA objetivo)
        currency: 'EUR',
      });
    }
  }

  // ─────────────────────────────────────────────────────────────────────
  // 4. REGISTRO DE USUARIO
  //    Cuándo: cuando Firebase Auth crea el usuario con éxito
  //    Dónde:  SignUp.vue → signUp() y signUpWithGoogle()
  // ─────────────────────────────────────────────────────────────────────
  function trackSignUp({ metodo = 'email', tipoUsuario = '' } = {}) {
    track('sign_up', {
      method: metodo,
      tipo_usuario: tipoUsuario,   // 'agent' | 'applicant' | 'owner'
    });
    // Meta: CompleteRegistration — señal de intención alta
    trackMeta('CompleteRegistration', {
      content_name: tipoUsuario,
      status: true,
    });
    // Google Ads: conversión secundaria — registro de usuario
    if (GOOGLE_ADS_CONVERSIONS.registro) {
      trackGoogleAdsConversion(GOOGLE_ADS_CONVERSIONS.registro, {
        value: 3,
        currency: 'EUR',
      });
    }
  }

  // ─────────────────────────────────────────────────────────────────────
  // 5. INICIO DE PAGO
  //    Cuándo: cuando se llama a Stripe para crear la sesión de checkout
  //    Dónde:  ColivingPayment.vue → initiateStripePayment()
  // ─────────────────────────────────────────────────────────────────────
  function trackBeginCheckout({ valor = 0, colivingId = '', colivingNombre = '', tipoPago = 'initial' } = {}) {
    track('begin_checkout', {
      currency: 'EUR',
      value: valor,
      items: [{
        item_id: colivingId,
        item_name: colivingNombre,
        item_category: 'coliving',
        price: valor,
        quantity: 1,
      }],
      tipo_pago: tipoPago,
    });
    // Meta: InitiateCheckout — señal de intención de pago
    trackMeta('InitiateCheckout', {
      content_ids: [colivingId],
      content_name: colivingNombre,
      num_items: 1,
      currency: 'EUR',
      value: valor,
    });
    // Google Ads: conversión de checkout — inicio de pago
    if (GOOGLE_ADS_CONVERSIONS.checkout) {
      trackGoogleAdsConversion(GOOGLE_ADS_CONVERSIONS.checkout, {
        value: valor,
        currency: 'EUR',
      });
    }
  }

  // ─────────────────────────────────────────────────────────────────────
  // 6. PAGO COMPLETADO
  //    Cuándo: tras confirmar el pago (webhook o redirect de Stripe)
  //    Dónde:  ViewContractPublic.vue o página de éxito de pago
  // ─────────────────────────────────────────────────────────────────────
  function trackPurchase({ transactionId = '', valor = 0, colivingId = '', colivingNombre = '' } = {}) {
    track('purchase', {
      transaction_id: transactionId,
      currency: 'EUR',
      value: valor,
      items: [{
        item_id: colivingId,
        item_name: colivingNombre,
        item_category: 'coliving',
        price: valor,
        quantity: 1,
      }],
    });
    // Meta: Purchase — conversión de mayor valor para optimización de campañas
    trackMeta('Purchase', {
      content_ids: [colivingId],
      content_name: colivingNombre,
      content_type: 'product',
      currency: 'EUR',
      value: valor,
    });
  }

  // ─────────────────────────────────────────────────────────────────────
  // 7. CAMBIO DE IDIOMA
  //    Cuándo: cuando el usuario cambia de español a inglés o viceversa
  //    Dónde:  LanguageSwitcherDropdown.vue → changeLanguage()
  // ─────────────────────────────────────────────────────────────────────
  function trackLanguageChange({ from = '', to = '', source = 'navbar' } = {}) {
    trackWithBackend('language_change', {
      language_from: from,
      language_to: to,
      source,
    });
  }

  // ─────────────────────────────────────────────────────────────────────
  // 8. CLICKS EN NAVEGACIÓN
  //    Cuándo: cuando el usuario hace click en enlaces del navbar
  //    Dónde:  App.vue → navegación central
  // ─────────────────────────────────────────────────────────────────────
  function trackNavClick({ section = '', label = '', destination = '' } = {}) {
    trackWithBackend('nav_click', {
      nav_section: section,
      nav_label: label,
      destination,
    });
  }

  // ─────────────────────────────────────────────────────────────────────
  // 9. CLICKS EN CARRUSEL DE HERO
  //    Cuándo: cuando el usuario hace click en una ciudad del carrusel
  //    Dónde:  HeroLanding.vue → carrusel de ciudades
  // ─────────────────────────────────────────────────────────────────────
  function trackHeroCarouselClick({ ciudad = '', position = 'carousel' } = {}) {
    trackWithBackend('hero_carousel_city_click', {
      ciudad,
      source: 'hero_landing',
      position,
    });
  }

  // ─────────────────────────────────────────────────────────────────────
  // 10. BÚSQUEDA DESDE HERO
  //     Cuándo: cuando se ejecuta búsqueda desde SearchBar del hero
  //     Dónde:  HeroLanding.vue → handleSearch()
  // ─────────────────────────────────────────────────────────────────────
  function trackHeroSearch({ ciudad = '', isLoggedIn = false } = {}) {
    track('hero_search', {
      ciudad: ciudad || 'todas',
      usuario_logueado: isLoggedIn,
      source: 'hero_landing_featured',
    });
  }

  // ─────────────────────────────────────────────────────────────────────
  // 11. CAMBIO DE TIPO DE USUARIO EN SIGNUP
  //     Cuándo: cuando el usuario cambia entre agent/applicant
  //     Dónde:  SignUp.vue → userType toggle
  // ─────────────────────────────────────────────────────────────────────
  function trackSignUpUserTypeChange({ newType = '', previousType = '' } = {}) {
    trackWithBackend('signup_user_type_change', {
      new_user_type: newType,
      previous_user_type: previousType,
      source: 'signup_page',
    });
  }

  // ─────────────────────────────────────────────────────────────────────
  // 12. ERROR EN SIGNUP
  //     Cuándo: cuando falla el registro por email o Google
  //     Dónde:  SignUp.vue → catch blocks
  // ─────────────────────────────────────────────────────────────────────
  function trackSignUpError({ method = 'email', errorCode = '', errorMessage = '', userType = '' } = {}) {
    trackWithBackend('signup_error', {
      method,
      error_code: errorCode,
      error_message: errorMessage.substring(0, 200), // Limitar longitud
      user_type: userType,
    });
  }

  // ─────────────────────────────────────────────────────────────────────
  // 13. ERROR EN LOGIN
  //     Cuándo: cuando falla el login por email o Google
  //     Dónde:  Login.vue → catch blocks
  // ─────────────────────────────────────────────────────────────────────
  function trackLoginError({ method = 'email', errorCode = '', errorMessage = '' } = {}) {
    trackWithBackend('login_error', {
      method,
      error_code: errorCode,
      error_message: errorMessage.substring(0, 200), // Limitar longitud
    });
  }

  // ─────────────────────────────────────────────────────────────────────
  // 14. CAMBIO DE FILTRO EN BÚSQUEDA
  //     Cuándo: cuando el usuario cambia un filtro individual
  //     Dónde:  ColivingAdvancedSearch.vue → filtros
  // ─────────────────────────────────────────────────────────────────────
  function trackFilterChange({ filterName = '', filterValue = null, ciudad = '' } = {}) {
    trackWithBackend('search_filter_change', {
      filter_name: filterName,
      filter_value: filterValue,
      ciudad: ciudad || 'todas',
    });
  }

  // ─────────────────────────────────────────────────────────────────────
  // 15. TOGGLE FILTROS AVANZADOS
  //     Cuándo: cuando el usuario abre/cierra los filtros avanzados
  //     Dónde:  ColivingAdvancedSearch.vue → showAdvancedFilters
  // ─────────────────────────────────────────────────────────────────────
  function trackAdvancedFiltersToggle({ isOpen = false, ciudad = '' } = {}) {
    trackWithBackend('search_advanced_filters_toggle', {
      action: isOpen ? 'open' : 'close',
      ciudad: ciudad || 'todas',
    });
  }

  // ─────────────────────────────────────────────────────────────────────
  // 16. BÚSQUEDA SIN NINGÚN CLICK (Punto 3)
  //     Cuándo: el usuario busca pero no hace click en ningún resultado
  //     Dónde:  ColivingAdvancedSearch.vue → beforeunload / nueva búsqueda
  //     Valor:  Detecta resultados irrelevantes o mala UX
  // ─────────────────────────────────────────────────────────────────────
  function trackSearchNoClick({ ciudad = '', region = '', totalResultados = 0 } = {}) {
    trackWithBackend('search_no_click', {
      city: ciudad || 'todas',
      region: region || 'es',
      resultsCount: totalResultados,
    });
    track('search_no_click', { ciudad, total_resultados: totalResultados });
  }

  // ─────────────────────────────────────────────────────────────────────
  // 17. TIEMPO HASTA PRIMERA BÚSQUEDA (Punto 6)
  //     Cuándo: primera vez que el usuario realiza una búsqueda real
  //     Dónde:  ColivingAdvancedSearch.vue → primera llamada a performSearch
  //     Valor:  Mide fricción del onboarding de búsqueda
  // ─────────────────────────────────────────────────────────────────────
  function trackFirstSearchDelay({ delaySeconds = 0 } = {}) {
    trackWithBackend('first_search_delay', { delaySeconds });
    track('first_search_delay', { delay_seconds: delaySeconds });
  }

  // ─────────────────────────────────────────────────────────────────────
  // 18. COMPARTIR COLIVING (Punto 10)
  //     Cuándo: usuario usa el botón de compartir o copia el enlace
  //     Dónde:  ContractCard.vue / ViewColivingPublic.vue
  //     Valor:  Mide viralidad orgánica del producto
  // ─────────────────────────────────────────────────────────────────────
  function trackShareColiving({ colivingId = '', ciudad = '', method = 'copy' } = {}) {
    trackWithBackend('share_coliving', {
      colivingId,
      city: ciudad || 'Sin ciudad',
      method,
    });
    track('share', { method, content_type: 'coliving', item_id: colivingId });
  }

  // ─────────────────────────────────────────────────────────────────────
  // 18. INTENCIÓN DE LEAD — Click en ContractCard externo
  //     Cuándo: usuario hace click en una tarjeta de coliving externo
  //             (scraped) → va a abrirse el modal ScrapedColivingLeadModal
  //     Dónde:  ContractCard.vue → handleCardClick / handleQuickPreviewCTA
  //     Valor:  Micro-conversión de intención alta; alimenta Smart Bidding
  //             sin esperar a que rellene el formulario completo.
  //             Google Ads lo usa para modelar "probabilidad de conversión".
  // ─────────────────────────────────────────────────────────────────────
  function trackScrapedLeadIntent({
    colivingId = '',
    colivingNombre = '',
    ciudad = '',
    posicion = undefined,
    fuente = '',
    origen = 'card-click',   // 'card-click' | 'quick-preview'
  } = {}) {
    track('select_item', {
      item_list_name: 'coliving_search_results',
      items: [{
        item_id: colivingId,
        item_name: colivingNombre,
        item_category: 'coliving',
        item_category2: ciudad,
        item_brand: fuente,
        index: posicion,
      }],
      coliving_id: colivingId,
      ciudad,
      fuente,
      origen,
    });
    // Evento custom para el embudo de Google Ads: coliving_lead_intent
    // Más granular que select_item; permite segmentar por ciudad/proveedor
    trackWithBackend('coliving_lead_intent', {
      coliving_id: colivingId,
      coliving_nombre: colivingNombre,
      ciudad,
      fuente,
      origen,
      posicion,
    });
  }

  // ─────────────────────────────────────────────────────────────────────
  // 19. MODAL DE LEAD ABIERTO — ScrapedColivingLeadModal
  //     Cuándo: el modal se muestra al usuario (mode listing o search)
  //     Dónde:  ScrapedColivingLeadModal.vue → watch(show)
  //     Valor:  Micro-conversión de intención muy alta (formulario visible)
  // ─────────────────────────────────────────────────────────────────────
  function trackLeadModalOpen({
    modo = 'search',    // 'search' | 'listing' | 'exit'
    colivingId = '',
    colivingNombre = '',
    ciudad = '',
    abVariant = '',
  } = {}) {
    track('coliving_lead_modal_open', {
      modo,
      coliving_id: colivingId,
      ciudad,
      ab_variant: abVariant,
    });
    // Meta: Lead con status 'form_open' — crea audiencia de alta intención
    trackMeta('Lead', {
      content_name: colivingNombre || ciudad,
      content_category: ciudad,
      status: 'form_open',
    });
  }

  // ─────────────────────────────────────────────────────────────────────
  // 20. LEAD ENVIADO — ScrapedColivingLeadModal
  //     Cuándo: submitListingLead / submitSearchLead devuelve ok:true
  //     Dónde:  ScrapedColivingLeadModal.vue → handleSubmit (éxito)
  //             useSearchLeadOrchestrator.js → silent submit (éxito)
  //     Valor:  MACRO-CONVERSIÓN principal para Google Ads y Meta Ads.
  //             Activa Smart Bidding Target CPA.
  // ─────────────────────────────────────────────────────────────────────
  function trackScrapedLeadSubmitted({
    colivingId = '',
    colivingNombre = '',
    ciudad = '',
    modo = 'listing',    // 'listing' | 'search' | 'exit' | 'silent'
    abVariant = '',
    leadId = '',
  } = {}) {
    // GA4: generate_lead — evento estándar e2commerce
    track('generate_lead', {
      coliving_id: colivingId,
      coliving_nombre: colivingNombre,
      ciudad,
      modo,
      ab_variant: abVariant,
      lead_id: leadId,
      currency: 'EUR',
      value: 12,   // valor estimado del lead; actualizar según CPA real
    });
    // Backend analytics (historial de leads del agente)
    trackWithBackend('coliving_lead_submitted', {
      coliving_id: colivingId,
      ciudad,
      modo,
      ab_variant: abVariant,
    });
    // Meta: Lead — conversión de alta prioridad
    trackMeta('Lead', {
      content_name: colivingNombre || ciudad,
      content_category: ciudad,
      content_ids: colivingId ? [colivingId] : [],
      currency: 'EUR',
      value: 12,
      status: 'submitted',
    });
    // Google Ads: conversión principal (Target CPA → Smart Bidding)
    if (GOOGLE_ADS_CONVERSIONS.solicitud) {
      trackGoogleAdsConversion(GOOGLE_ADS_CONVERSIONS.solicitud, {
        value: 12,
        currency: 'EUR',
        transaction_id: leadId || undefined,
      });
    }
  }

  return {
    track,
    trackMeta,
    trackMetaCustom,
    trackSearch,
    trackZeroResults,
    trackLoadMore,
    trackViewColiving,
    trackApply,
    trackSignUp,
    trackBeginCheckout,
    trackPurchase,
    trackLanguageChange,
    trackNavClick,
    trackHeroCarouselClick,
    trackHeroSearch,
    trackSignUpUserTypeChange,
    trackSignUpError,
    trackLoginError,
    trackFilterChange,
    trackAdvancedFiltersToggle,
    trackSearchNoClick,
    trackFirstSearchDelay,
    trackShareColiving,
    trackScrapedLeadIntent,
    trackLeadModalOpen,
    trackScrapedLeadSubmitted,
  };
}
